import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowRight, ArrowLeft, Minus, Plus, X as XIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const plans = [
  { id: "gratuit", name: "Gratuit", price: "0€", sub: "Pour découvrir Prosperr", cta: "Commencer gratuitement", items: ["10 leads maximum", "3 emails commerciaux par mois", "1 recherche géolocalisée"] },
  { id: "independant", name: "Indépendant", price: "19,90€", sub: "La formule la plus choisie", cta: "Essayer 14 jours", hl: true, items: ["Leads illimités", "Toutes les fonctions Prosperr", "3 canaux de prospection", "Export Excel et CSV", "Mode hors ligne (application installable)"] },
  { id: "entreprise", name: "Entreprise", price: "49€", sub: "Pour équipes commerciales", cta: "Essayer 14 jours", items: ["Tout le plan Indépendant", "Jusqu'à 10 commerciaux", "Pipeline partagé en équipe", "Tableau de bord manager", "Rapport hebdomadaire automatique"] },
  { id: "whitelabel", name: "White Label", price: "99€", sub: "Pour revendeurs et agences", cta: "Nous contacter", items: ["Tout le plan Entreprise", "Logo et domaine personnalisés", "Tableau de bord revendeur", "Gestion clients illimitée"] },
];

const matrix = [
  { label: "Nombre de leads maximum", values: ["10", "Illimité", "Illimité", "Illimité"] },
  { label: "Emails commerciaux Prosperr par mois", values: ["3", "Illimité", "Illimité", "Illimité"] },
  { label: "Prospection Road Maps (carte + Prosperr)", values: ["1 recherche", true, true, true] },
  { label: "Scripts téléphone personnalisés", values: [false, true, true, true] },
  { label: "Messages LinkedIn, WhatsApp, Facebook, Instagram", values: [false, true, true, true] },
  { label: "Mode visite client (face-à-face)", values: [false, true, true, true] },
  { label: "Analyse de réponses prospects", values: [false, true, true, true] },
  { label: "Action Booster (sprint commercial)", values: [false, true, true, true] },
  { label: "Export Excel et CSV", values: [false, true, true, true] },
  { label: "Import de fichier avec scoring automatique", values: [false, true, true, true] },
  { label: "Application installable et mode hors ligne", values: [true, true, true, true] },
  { label: "Nombre d'utilisateurs", values: ["1", "1", "Jusqu'à 10", "Illimité"] },
  { label: "Pipeline partagé en équipe", values: [false, false, true, true] },
  { label: "Tableau de bord manager", values: [false, false, true, true] },
  { label: "Rapport hebdomadaire automatique", values: [false, false, true, true] },
  { label: "Logo et domaine personnalisés", values: [false, false, false, true] },
  { label: "Tableau de bord revendeur", values: [false, false, false, true] },
  { label: "Support client", values: ["Communauté", "Email", "Email prioritaire", "Dédié"] },
];

const faqs = [
  { q: "L'essai gratuit de 14 jours nécessite-t-il une carte bancaire ?", a: "Non. Vous créez votre compte Prosperr en 30 secondes, sans carte bancaire. À la fin de la période d'essai, vous choisissez librement de continuer ou non. Aucun prélèvement automatique." },
  { q: "Puis-je changer de formule à tout moment ?", a: "Oui, en un seul clic depuis votre espace. La montée en gamme est immédiate, le passage à une formule inférieure prend effet à la fin de la période de facturation en cours." },
  { q: "Comment fonctionne la facturation et la TVA ?", a: "Une facture conforme à la réglementation européenne et incluant la TVA est émise automatiquement chaque mois. Vous pouvez la télécharger directement depuis votre espace de facturation." },
];

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className="w-full text-left border-b border-[var(--border)] py-6 group"
      data-testid={`faq-${q.slice(0, 10)}`}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="font-display text-xl sm:text-2xl font-medium text-[var(--text)] leading-tight">{q}</div>
        <div className="mt-1 shrink-0 w-8 h-8 rounded-full border border-[var(--border)] grid place-items-center group-hover:border-emerald-500 group-hover:text-emerald-300 transition-colors text-zinc-400">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </div>
      </div>
      {open && <div className="text-base text-zinc-400 mt-4 leading-relaxed max-w-2xl">{a}</div>}
    </button>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState("");

  const subscribe = async (planId) => {
    if (planId === "gratuit") { navigate("/register"); return; }
    if (!user) { navigate("/register"); return; }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/stripe/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Erreur Stripe");
      setLoadingPlan("");
    }
  };

  const CellValue = ({ v }) => {
    if (v === true) return <Check className="w-5 h-5 text-[var(--text)] mx-auto" strokeWidth={2.4} />;
    if (v === false) return <XIcon className="w-4 h-4 text-zinc-700 mx-auto" />;
    return <span className="text-sm text-[var(--text)] font-medium">{v}</span>;
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] antialiased">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-display font-semibold text-xl tracking-tight">Prosperr</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="ghost" size="sm" className="rounded-full text-zinc-400 hover:text-white hover:bg-[var(--surface-alt)]" data-testid="back">
                <ArrowLeft className="w-4 h-4 mr-1.5" />Accueil
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full border-[var(--border)] bg-transparent text-zinc-300 hover:bg-emerald-500 hover:text-white hover:border-emerald-500">
                Se connecter
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Tarifs Prosperr</div>
            <h1 className="mt-3 font-display font-bold text-[var(--text)] tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              Tarifs simples et transparents.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              14 jours d'essai gratuit, sans carte bancaire. Changez de formule ou annulez en un clic, à tout moment.
            </p>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[var(--border)]">
            {plans.map((p, i) => (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`p-8 flex flex-col relative ${
                  p.hl ? "bg-emerald-500 text-white" : "bg-[var(--surface)] text-[var(--text)]"
                } ${i > 0 ? "lg:border-l border-[var(--border)]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[var(--border)]" : ""}`}
              >
                {p.hl && (
                  <div className="absolute -top-3 left-8 bg-white text-emerald-600 text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1">
                    Populaire
                  </div>
                )}
                <div className={`text-xs font-bold uppercase tracking-widest ${p.hl ? "text-white/70" : "text-zinc-500"}`}>
                  {p.sub}
                </div>
                <div className={`font-display font-bold text-2xl mt-1 ${p.hl ? "text-white" : "text-[var(--text)]"}`}>
                  {p.name}
                </div>
                <div className={`font-display font-bold text-5xl mt-6 tracking-tight ${p.hl ? "text-white" : "text-[var(--text)]"}`}>
                  {p.price}
                  <span className={`text-sm font-normal ${p.hl ? "text-white/70" : "text-zinc-500"}`}>/mois</span>
                </div>
                <ul className="space-y-3 mt-8 flex-1">
                  {p.items.map((it) => (
                    <li key={it} className={`flex gap-3 text-sm ${p.hl ? "text-white/90" : "text-zinc-300"}`}>
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.hl ? "text-white" : "text-emerald-400"}`} strokeWidth={2.4} />
                      {it}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(p.id)}
                  disabled={loadingPlan === p.id}
                  data-testid={`subscribe-${p.id}`}
                  className={`w-full rounded-full mt-8 h-12 text-base font-medium ${
                    p.hl
                      ? "bg-white text-emerald-600 hover:bg-zinc-100"
                      : "bg-[var(--surface-alt)] text-[var(--text)] border border-[var(--border)] hover:border-zinc-700"
                  }`}
                >
                  {loadingPlan === p.id ? "…" : p.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison matrix */}
      <section className="border-b border-[var(--border)] bg-[var(--surface-alt)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24">
          <div className="max-w-3xl mb-12">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Comparatif</div>
            <h2 className="mt-3 font-display font-bold text-[var(--text)] tracking-tight leading-[0.95] text-4xl sm:text-5xl">
              Toutes les fonctionnalités.
            </h2>
          </div>
          <div className="border border-[var(--border)] overflow-x-auto bg-[var(--surface)]">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="p-5 text-left font-display font-bold text-[var(--text)] text-sm"></th>
                  {plans.map((p) => (
                    <th
                      key={p.id}
                      className={`p-5 text-center font-display font-bold text-sm border-l border-[var(--border)] ${
                        p.hl ? "bg-emerald-500 text-white" : "text-[var(--text)]"
                      }`}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.map((row, i) => (
                  <tr key={i} className="border-b border-[var(--border)] last:border-b-0" data-testid={`matrix-row-${i}`}>
                    <td className="p-4 text-sm text-zinc-200 font-medium">{row.label}</td>
                    {row.values.map((v, j) => (
                      <td key={j} className={`p-4 text-center border-l border-[var(--border)] ${plans[j].hl ? "bg-emerald-500/10" : ""}`}>
                        <CellValue v={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24 grid lg:grid-cols-3 gap-16">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Questions fréquentes</div>
            <h2 className="mt-3 font-display font-bold text-[var(--text)] tracking-tight leading-[0.95] text-4xl sm:text-5xl">
              Tout savoir avant de choisir votre formule.
            </h2>
          </div>
          <div className="lg:col-span-2">
            {faqs.map((f, i) => <Faq key={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--surface-alt)] text-white relative overflow-hidden">
        <div className="brand-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 py-20 sm:py-24 relative">
          <div className="max-w-3xl">
            <h2 className="font-display font-bold text-white tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              Lancez votre prospection commerciale dès aujourd'hui.
            </h2>
            <div className="mt-10">
              <Link to="/register">
                <Button
                  data-testid="final-cta"
                  className="hp-btn hp-btn-primary h-14 px-8 text-base"
                >
                  Essayer Prosperr gratuitement 14 jours
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
